import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Warehouse, MapPin, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { CrossTenantNotice } from '@/components/common/CrossTenantNotice';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Toolbar } from '@/components/common/Toolbar';
import { DataTable, type Column } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatusFilter } from '@/components/common/StatusFilter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PIIField } from '@/components/common/PIIField';
import { platformWarehousesService } from '@/services/platform/warehouses';
import type { PlatformWarehouse } from '@/types/platform';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { PlatformWarehouseFormSheet } from './PlatformWarehouseFormSheet';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export function PlatformWarehousesPage() {
  const me = useCurrentUser();
  const isPlatformAdmin = me?.tier === 'PLATFORM_ADMIN';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformWarehouse | null>(null);
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['platform', 'warehouses', { q: debounced, status }],
    queryFn: () =>
      platformWarehousesService.list({
        q: debounced,
        status: status === 'all' ? undefined : status,
        pageSize: 50,
      }),
  });

  // H1: per-plan warehouse cap. Only fetch for tenant-admin — the
  // platform-admin view is cross-tenant so a single quota doesn't make
  // sense there. Tenant-admin's create button is gated on this.
  const quotaQuery = useQuery({
    queryKey: ['platform', 'warehouses', 'quota'],
    queryFn: () => platformWarehousesService.quota(),
    enabled: !isPlatformAdmin,
  });
  const quota = quotaQuery.data;
  const atLimit = !!quota?.atLimit;

  const columns: Column<PlatformWarehouse>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (r) => <span className="font-mono text-xs">{r.code}</span>,
      width: '140px',
    },
    {
      key: 'name',
      header: 'Warehouse',
      cell: (r) => (
        <div className="flex items-center gap-3">
          {r.imageUrl ? (
            <img
              src={r.imageUrl}
              alt=""
              className="h-9 w-9 shrink-0 rounded-md border border-border/60 object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-muted/40 text-muted-foreground/50"
            >
              <Warehouse className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <div className="truncate font-medium text-foreground">{r.name}</div>
              {r.type && r.type !== 'warehouse' && (
                <Badge variant="muted" className="text-[10px] uppercase">
                  {r.type.replace('_', ' ')}
                </Badge>
              )}
            </div>
            {r.address?.city && (
              <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {[r.address.city, r.address.state].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'org',
      header: 'Organization',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-sm">{r.organizationName ?? r.organizationId}</div>
          <div className="text-xs text-muted-foreground">{r.tenantName ?? r.tenantId}</div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-sm">{r.contactPerson ?? '—'}</div>
          <PIIField
            value={r.contactPhone}
            kind="phone"
            entity="warehouse"
            entityId={r.id}
            fieldName="contactPhone"
          />
        </div>
      ),
    },
    {
      key: 'created',
      header: 'Created at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Workspace"
        title="Warehouses"
        description={
          isPlatformAdmin
            ? 'Cross-tenant warehouse directory — deep-link only. Routine investigation should drill into a specific tenant.'
            : 'Physical fulfilment locations in your workspace.'
        }
        actions={
          isPlatformAdmin ? null : (
            <div className="flex items-center gap-2">
              {quota && (
                <Badge variant={atLimit ? 'destructive' : 'muted'} className="font-mono tabular-nums">
                  {quota.used}
                  {quota.max !== null ? ` of ${quota.max}` : ''}
                  {' used'}
                </Badge>
              )}
              <Button
                size="sm"
                onClick={() => setCreateOpen(true)}
                disabled={atLimit}
                title={
                  atLimit
                    ? `Your ${quota?.planName} plan allows ${quota?.max} warehouse${quota?.max === 1 ? '' : 's'}. Upgrade to add more.`
                    : 'Create a fulfilment location'
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Add warehouse
              </Button>
            </div>
          )
        }
      />

      {!isPlatformAdmin && atLimit && quota && (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/[0.05] p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1 text-[12.5px]">
            <div className="font-semibold">
              You're at the {quota.planName} plan limit ({quota.max} warehouse
              {quota.max === 1 ? '' : 's'})
            </div>
            <p className="text-muted-foreground">
              Upgrade your plan to add more fulfilment locations. Each plan
              tier includes a higher warehouse cap.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/plan">
              Compare plans <ArrowUpRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      )}

      {isPlatformAdmin && (
        <CrossTenantNotice
          resourceLabel="customer warehouse addresses & contacts"
          rationale="Addresses and on-site contact people are PII the customer controls."
        />
      )}

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search warehouse code, name, city…"
        filters={<StatusFilter value={status} onChange={setStatus} options={STATUS_OPTIONS} />}
      />

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.items}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          onRowClick={(r) => setEditing(r)}
          emptyTitle="No warehouses yet"
          emptyDescription="Add the first fulfilment location."
          emptyAction={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Warehouse className="h-3.5 w-3.5" />
              Add warehouse
            </Button>
          }
        />
      )}

      <PlatformWarehouseFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      <PlatformWarehouseFormSheet
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        existing={editing}
      />
    </div>
  );
}
